import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, Mail, Shield, Truck, Award, Target, Users, Globe } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hero Video Background
const HeroVideo = () => {
  const videoRef = React.useRef(null);
  const [videoLoaded, setVideoLoaded] = React.useState(false);
  const videoSrc = `/hero-video.mp4`;
  const posterSrc = `/hero-poster.jpg`;

  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onCanPlay = () => { setVideoLoaded(true); v.play().catch(() => {}); };
    v.addEventListener('canplay', onCanPlay);
    v.load();
    return () => v.removeEventListener('canplay', onCanPlay);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: '#0a0f1a' }}>
      <img src={posterSrc} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: videoLoaded ? 0 : 1, transition: 'opacity 1s' }} />
      <video ref={videoRef} muted loop playsInline preload="auto" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: videoLoaded ? 1 : 0, transition: 'opacity 1s' }}>
        <source src={videoSrc} type="video/mp4" />
      </video>
      {/* Only left gradient for text readability - no darkening on the rest */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(10,15,26,0.65) 0%, rgba(10,15,26,0.3) 35%, rgba(10,15,26,0.05) 60%, transparent 100%)' }} />
      <div className="absolute bottom-0 left-0 right-0 h-12" style={{ background: 'linear-gradient(to top, #0F172A, transparent)' }} />
    </div>
  );
};

export const HomePage = () => {
  const { language, getLocalizedField } = useLanguage();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/categories`).then(r => setCategories(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const texts = {
    nl: {
      title: 'Engineering Excellence\nin Metal Profiles',
      subtitle: 'Toonaangevende leverancier van profielsystemen voor de professionele bouwsector in de Benelux.',
      cta1: 'Assortiment',
      cta2: 'Offerte Aanvragen',
      // Expertise section
      expertiseLabel: 'EXPERTISE',
      expertiseTitle: 'Waarvoor u bij ons terecht kunt',
      exp1Title: 'CW/UW Wandprofielen', exp1Text: 'Verticale stijl- en horizontale randprofielen voor niet-dragende scheidingswanden.',
      exp2Title: 'CD/UD Plafondprofielen', exp2Text: 'Draag- en randprofielen voor professionele systeemplafonds conform Europese normen.',
      exp3Title: 'Hoek- en Afwerkprofielen', exp3Text: 'Aluminium en stalen afwerkprofielen voor een perfect eindresultaat.',
      // Values
      valLabel: 'KERNWAARDEN',
      valTitle: 'Wat ons onderscheidt',
      val1Title: 'Kwaliteitsborging', val1Text: 'CE-gecertificeerde materialen, Z100 verzinkt staal. Volledige traceerbaarheid van grondstof tot levering.',
      val2Title: 'Logistieke betrouwbaarheid', val2Text: 'Efficiante distributie vanuit Antwerpen naar heel Belgie en de omliggende regio\'s.',
      val3Title: 'Technisch advies', val3Text: 'Persoonlijke begeleiding bij productkeuze en toepassingsadvies door onze specialisten.',
      val4Title: 'Partnerschap', val4Text: 'Langetermijnrelaties met aannemers, installateurs en bouwbedrijven in de Benelux.',
      // CTA
      ctaTitle: 'Samenwerken?',
      ctaText: 'Neem contact op met ons team voor technisch advies of een offerte op maat.',
      // Vision
      visionLabel: 'ONZE VISIE',
      visionTitle: 'Belgische expertise, Duitse precisie',
      visionText: 'Alphaprofiles combineert 30 jaar Belgische ervaring in de bouwsector met 20 jaar Duitse marktervaring. Door onze strategische samenwerking met Duitse partners brengen wij de betrouwbaarheid van Europese kwaliteitsnormen samen met lokale marktkennis.',
      visionText2: 'Deze unieke combinatie stelt ons in staat om de Belgische, Nederlandse en Duitse markt te bedienen met profielsystemen die voldoen aan de strengste eisen van de professionele bouwsector.',
      visionStat1: 'Belgische marktervaring', visionStat1v: '30+',
      visionStat2: 'Duitse marktervaring', visionStat2v: '20+',
      visionStatUnit: 'jaar',
    },
    en: {
      title: 'Engineering Excellence\nin Metal Profiles',
      subtitle: 'Leading supplier of profile systems for the professional construction sector in the Benelux.',
      cta1: 'Product Range',
      cta2: 'Request Quote',
      expertiseLabel: 'EXPERTISE',
      expertiseTitle: 'What we deliver',
      exp1Title: 'CW/UW Wall Profiles', exp1Text: 'Vertical stud and horizontal track profiles for non-load-bearing partition walls.',
      exp2Title: 'CD/UD Ceiling Profiles', exp2Text: 'Load-bearing and edge profiles for professional suspended ceiling systems.',
      exp3Title: 'Corner & Finishing Profiles', exp3Text: 'Aluminum and steel finishing profiles for a perfect end result.',
      valLabel: 'CORE VALUES',
      valTitle: 'What sets us apart',
      val1Title: 'Quality Assurance', val1Text: 'CE-certified materials, Z100 galvanized steel. Full traceability from raw material to delivery.',
      val2Title: 'Logistic Reliability', val2Text: 'Efficient distribution from Antwerp to all of Belgium and surrounding regions.',
      val3Title: 'Technical Advice', val3Text: 'Personal guidance on product selection and application advice from our specialists.',
      val4Title: 'Partnership', val4Text: 'Long-term relationships with contractors, installers and construction companies in the Benelux.',
      ctaTitle: 'Let\'s collaborate',
      ctaText: 'Contact our team for technical advice or a customized quote.',
      visionLabel: 'OUR VISION',
      visionTitle: 'Belgian expertise, German precision',
      visionText: 'Alphaprofiles combines 30 years of Belgian experience in the construction sector with 20 years of German market experience. Through our strategic partnership with German partners, we bring together the reliability of European quality standards with local market knowledge.',
      visionText2: 'This unique combination enables us to serve the Belgian, Dutch and German markets with profile systems that meet the strictest requirements of the professional construction sector.',
      visionStat1: 'Belgian market experience', visionStat1v: '30+',
      visionStat2: 'German market experience', visionStat2v: '20+',
      visionStatUnit: 'years',
    },
    fr: {
      title: 'Engineering Excellence\nen Profils Metalliques',
      subtitle: 'Fournisseur de reference de systemes de profils pour le secteur professionnel de la construction au Benelux.',
      cta1: 'Gamme de Produits',
      cta2: 'Demander un Devis',
      expertiseLabel: 'EXPERTISE',
      expertiseTitle: 'Ce que nous offrons',
      exp1Title: 'Profils CW/UW Muraux', exp1Text: 'Profils montants verticaux et rails horizontaux pour cloisons non porteuses.',
      exp2Title: 'Profils CD/UD Plafond', exp2Text: 'Profils porteurs et de rive pour plafonds suspendus professionnels.',
      exp3Title: 'Profils d\'Angle & Finition', exp3Text: 'Profils de finition en aluminium et acier pour un resultat parfait.',
      valLabel: 'VALEURS FONDAMENTALES',
      valTitle: 'Ce qui nous distingue',
      val1Title: 'Assurance Qualite', val1Text: 'Materiaux certifies CE, acier galvanise Z100. Tracabilite complete.',
      val2Title: 'Fiabilite Logistique', val2Text: 'Distribution efficace depuis Anvers vers toute la Belgique et les regions environnantes.',
      val3Title: 'Conseil Technique', val3Text: 'Accompagnement personnalise dans le choix des produits par nos specialistes.',
      val4Title: 'Partenariat', val4Text: 'Relations a long terme avec les entrepreneurs et entreprises de construction.',
      ctaTitle: 'Collaborons',
      ctaText: 'Contactez notre equipe pour un conseil technique ou un devis sur mesure.',
      visionLabel: 'NOTRE VISION',
      visionTitle: 'Expertise belge, precision allemande',
      visionText: 'Alphaprofiles combine 30 ans d\'experience belge dans le secteur de la construction avec 20 ans d\'experience sur le marche allemand. Grace a notre partenariat strategique avec des partenaires allemands, nous reunissons la fiabilite des normes de qualite europeennes avec la connaissance du marche local.',
      visionText2: 'Cette combinaison unique nous permet de desservir les marches belge, neerlandais et allemand avec des systemes de profils repondant aux exigences les plus strictes du secteur professionnel de la construction.',
      visionStat1: 'Experience marche belge', visionStat1v: '30+',
      visionStat2: 'Expertise production allemande', visionStat2v: '20+',
      visionStatUnit: 'ans',
    },
    de: {
      title: 'Engineering Excellence\nin Metallprofilen',
      subtitle: 'Fuhrender Lieferant von Profilsystemen fur den professionellen Bausektor in der Benelux-Region.',
      cta1: 'Produktsortiment',
      cta2: 'Angebot Anfordern',
      expertiseLabel: 'EXPERTISE',
      expertiseTitle: 'Was wir liefern',
      exp1Title: 'CW/UW Wandprofile', exp1Text: 'Vertikale Stand- und horizontale Fuhrungsprofile fur nicht tragende Trennwande.',
      exp2Title: 'CD/UD Deckenprofile', exp2Text: 'Trag- und Randprofile fur professionelle abgehangte Deckensysteme.',
      exp3Title: 'Eck- und Abschlussprofile', exp3Text: 'Aluminium- und Stahl-Abschlussprofile fur ein perfektes Endergebnis.',
      valLabel: 'KERNWERTE',
      valTitle: 'Was uns auszeichnet',
      val1Title: 'Qualitatssicherung', val1Text: 'CE-zertifizierte Materialien, Z100 verzinkter Stahl. Vollstandige Ruckverfolgbarkeit.',
      val2Title: 'Logistische Zuverlassigkeit', val2Text: 'Effiziente Distribution von Antwerpen in ganz Belgien und die umliegenden Regionen.',
      val3Title: 'Technische Beratung', val3Text: 'Personliche Begleitung bei der Produktauswahl durch unsere Spezialisten.',
      val4Title: 'Partnerschaft', val4Text: 'Langfristige Beziehungen mit Auftragnehmern und Bauunternehmen in der Benelux.',
      ctaTitle: 'Zusammenarbeiten',
      ctaText: 'Kontaktieren Sie unser Team fur technische Beratung oder ein individuelles Angebot.',
      visionLabel: 'UNSERE VISION',
      visionTitle: 'Belgische Expertise, deutsche Prazision',
      visionText: 'Alphaprofiles vereint 30 Jahre belgische Erfahrung im Bausektor mit 20 Jahren deutscher Markterfahrung. Durch unsere strategische Zusammenarbeit mit deutschen Partnern verbinden wir die Zuverlassigkeit europaischer Qualitatsstandards mit lokaler Marktkenntnis.',
      visionText2: 'Diese einzigartige Kombination ermoglicht es uns, den belgischen, niederlandischen und deutschen Markt mit Profilsystemen zu bedienen, die den strengsten Anforderungen des professionellen Bausektors entsprechen.',
      visionStat1: 'Belgische Markterfahrung', visionStat1v: '30+',
      visionStat2: 'Deutsche Markterfahrung', visionStat2v: '20+',
      visionStatUnit: 'Jahre',
    },
  };

  const t = texts[language] || texts.nl;

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="min-h-screen flex items-center relative overflow-hidden bg-[#0F172A]" data-testid="hero-section">
        <HeroVideo />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-20 w-full">
          <motion.div className="max-w-2xl" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}>
            <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-extrabold text-white leading-[1.1] tracking-tighter mb-6 whitespace-pre-line">
              {t.title}
            </h1>
            <p className="text-base text-white/50 mb-10 max-w-lg leading-relaxed">
              {t.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/products">
                <button className="group inline-flex items-center gap-2 bg-[#BE1622] hover:bg-[#9E111B] text-white font-semibold tracking-wider uppercase text-[11px] px-8 py-4 transition-all">
                  {t.cta1} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </Link>
              <Link to="/contact">
                <button className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white/80 hover:text-white font-medium tracking-wider uppercase text-[11px] px-8 py-4 transition-all">
                  {t.cta2}
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Expertise - 3 Pillars */}
      <section className="py-20 lg:py-24 bg-white" data-testid="expertise-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-xl mb-14">
            <span className="corp-label block mb-3">{t.expertiseLabel}</span>
            <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">{t.expertiseTitle}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-gray-200">
            {[
              { icon: <Target className="w-5 h-5" />, title: t.exp1Title, text: t.exp1Text },
              { icon: <Shield className="w-5 h-5" />, title: t.exp2Title, text: t.exp2Text },
              { icon: <Award className="w-5 h-5" />, title: t.exp3Title, text: t.exp3Text },
            ].map((item, i) => (
              <div key={i} className={`p-8 lg:p-10 ${i > 0 ? 'md:border-l border-t md:border-t-0 border-gray-200' : ''}`}>
                <div className="w-10 h-10 bg-[#BE1622]/10 flex items-center justify-center text-[#BE1622] mb-5">{item.icon}</div>
                <h3 className="text-sm font-bold text-slate-900 mb-2 tracking-tight">{item.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision - Belgium x Germany */}
      <section className="py-20 lg:py-24 bg-[#0F172A]" data-testid="vision-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left: Stats */}
            <div className="lg:col-span-4">
              <span className="corp-label text-[#BE1622] block mb-6">{t.visionLabel}</span>
              <div className="space-y-8">
                <div>
                  <div className="text-5xl lg:text-6xl font-extrabold text-white tracking-tighter">{t.visionStat1v}</div>
                  <div className="text-xs text-white/40 uppercase tracking-wider mt-1">{t.visionStatUnit} {t.visionStat1}</div>
                </div>
                <div className="w-12 h-px bg-[#BE1622]" />
                <div>
                  <div className="text-5xl lg:text-6xl font-extrabold text-white tracking-tighter">{t.visionStat2v}</div>
                  <div className="text-xs text-white/40 uppercase tracking-wider mt-1">{t.visionStatUnit} {t.visionStat2}</div>
                </div>
              </div>
            </div>
            {/* Right: Text */}
            <div className="lg:col-span-8">
              <h2 className="text-2xl lg:text-3xl font-bold text-white tracking-tight mb-6">{t.visionTitle}</h2>
              <p className="text-white/50 text-sm leading-relaxed mb-4">{t.visionText}</p>
              <p className="text-white/50 text-sm leading-relaxed">{t.visionText2}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values - 4 Grid */}
      <section className="py-20 lg:py-24 bg-[#F8FAFC]" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-xl mb-14">
            <span className="corp-label block mb-3">{t.valLabel}</span>
            <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">{t.valTitle}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 border border-gray-200">
            {[
              { icon: <Shield className="w-5 h-5" />, title: t.val1Title, text: t.val1Text },
              { icon: <Truck className="w-5 h-5" />, title: t.val2Title, text: t.val2Text },
              { icon: <Users className="w-5 h-5" />, title: t.val3Title, text: t.val3Text },
              { icon: <Globe className="w-5 h-5" />, title: t.val4Title, text: t.val4Text },
            ].map((item, i) => (
              <div key={i} className="bg-white p-7 lg:p-8">
                <div className="text-[#BE1622] mb-4">{item.icon}</div>
                <h3 className="text-sm font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-500 text-xs leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-24 bg-[#0F172A]" data-testid="cta-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-white tracking-tight mb-4">{t.ctaTitle}</h2>
          <p className="text-white/40 mb-10 text-sm">{t.ctaText}</p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/contact">
              <button className="inline-flex items-center gap-2 bg-[#BE1622] hover:bg-[#9E111B] text-white font-semibold tracking-wider uppercase text-[11px] px-8 py-4 transition-all">
                <Mail className="w-4 h-4" /> info@alphaprofiles.com
              </button>
            </Link>
            <a href="tel:+32475223211">
              <button className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white/70 hover:text-white font-medium tracking-wider uppercase text-[11px] px-8 py-4 transition-all">
                <Phone className="w-4 h-4" /> +32 475 22 32 11
              </button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
;
